import SaveIcon from "@mui/icons-material/Save";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import FeeScheduleBillingCode from "_shared/fee-schedules/categories/billing-codes/fee-schedule-billing-code";
import FeeScheduleCategoryDescription from "_shared/fee-schedules/categories/descriptions/fee-schedule-category-description";
import Fee from "_shared/fee-schedules/categories/fees/fee";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import Guid from "_shared/guids/guid";
import CurrencyInput from "_shared/money/currencies/_views/currency-input";
import { Currency } from "_shared/money/currencies/currency";
import Money from "_shared/money/money";
import { useState } from "react";
import { useSession } from "users/__session/session-context";
import { FeeScheduleCategoryMaskedCurrency } from "work/_shared/masked-fields";
import FeeScheduleCategory from "../fee-schedule-category";

const FormContainer = styled("div")(() => ({}));
const NameTextField = styled(TextField)(({ theme }) => ({
  margin: 0,
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const DescriptionTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const PerSymbol = styled(Grid)(({ theme }) => ({
  alignSelf: "baseline",
  fontSize: "2em",
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));
const BillingCodeField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(2),
  width: "100%",
}));
const FeeRate = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));
const ButtonContainer = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(4),
  "& > button:not(:last-of-type)": {
    marginRight: theme.spacing(2),
  },
}));

type CreateFeeScheduleCategoryProps = {
  category?: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency;
  onCategoryAdded?: (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => void;
  onCategoryUpdated?: (
    originalName: FeeScheduleCategoryName | null,
    category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
  ) => void;
  popDialog?: () => void;
};

export default function CreateFeeScheduleCategory(props: Readonly<CreateFeeScheduleCategoryProps>) {
  const { category, onCategoryAdded, onCategoryUpdated, popDialog } = props;

  const [formDirty, setFormDirty] = useState(false);
  const [formName, setFormName] = useState<string>(category?.name?.value ?? "");
  const [formDescription, setFormDescription] = useState<string>(category?.description?.value ?? "");
  const [formBillingCode, setFormBillingCode] = useState<string>(category?.billingCode?.value ?? "");

  const [formFeeRate, setFormFeeRate] = useState<Money | undefined>(
    category instanceof FeeScheduleCategoryMaskedCurrency
      ? undefined
      : (category?.fee?.rate ?? new Money(0, Currency.USD)),
  );
  const [formFeeUnit, setFormFeeUnit] = useState<string | undefined>(
    category instanceof FeeScheduleCategoryMaskedCurrency ? undefined : (category?.fee?.unit ?? ""),
  );
  const [isFixedFee, setIsFixedFee] = useState<boolean | undefined>(
    category instanceof FeeScheduleCategoryMaskedCurrency ? undefined : Boolean(category && !category.fee?.unit),
  );
  const [isDeferred, setIsDeferred] = useState<boolean | undefined>(
    category instanceof FeeScheduleCategoryMaskedCurrency ? undefined : !category?.fee,
  );

  const session = useSession();

  function formValid(): boolean {
    if (category instanceof FeeScheduleCategoryMaskedCurrency) {
      return Boolean(formName && formDescription);
    }
    return Boolean(
      formName &&
      formDescription &&
      (isDeferred || (formFeeRate && (isFixedFee || (formFeeUnit && formFeeUnit?.length > 0)))),
    );
  }

  function handleAddCategoryClicked(): void {
    const newCategory = getStateAsFeeScheduleCategory();
    if (!newCategory) return;
    onCategoryAdded?.(newCategory);
    popDialog?.();
  }

  function handleSaveCategoryClicked(originalName: FeeScheduleCategoryName | null): void {
    if (!category) return;

    const updatedCategory = getStateAsFeeScheduleCategory();
    if (!updatedCategory) return;
    onCategoryUpdated?.(originalName, updatedCategory);
    popDialog?.();
  }

  function getStateAsFeeScheduleCategory(): FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency | undefined {
    if (!formName || !formDescription) return;
    if (category instanceof FeeScheduleCategoryMaskedCurrency) {
      return new FeeScheduleCategoryMaskedCurrency({
        id: category.id,
        name: new FeeScheduleCategoryName(formName),
        description: new FeeScheduleCategoryDescription(formDescription),
        billingCode: formBillingCode ? new FeeScheduleBillingCode(formBillingCode) : null,
      });
    }
    if (formFeeRate === undefined || formFeeUnit === undefined || isFixedFee === undefined || isDeferred === undefined)
      return;
    let fee: Fee | null;
    if (isDeferred) {
      fee = null;
    } else if (isFixedFee) {
      fee = Fee.create({ rate: formFeeRate, unit: null });
    } else {
      fee = Fee.create({ rate: formFeeRate, unit: formFeeUnit });
    }

    return new FeeScheduleCategory({
      name: new FeeScheduleCategoryName(formName),
      description: new FeeScheduleCategoryDescription(formDescription),
      fee,
      billingCode: formBillingCode ? new FeeScheduleBillingCode(formBillingCode) : null,
      id: category?.id ?? Guid.generate(),
    });
  }

  function getDeferredLabel(): string {
    const accountType = session.context?.viewingAsVendor ? "client" : "vendor";
    if (!isDeferred) return `Let ${accountType} set fee?`;
    return `${accountType[0].toUpperCase() + accountType.slice(1)} to set fee`;
  }

  return (
    <FormContainer>
      <NameTextField
        label="Category"
        sx={{ marginTop: 1 }}
        value={formName}
        required
        onChange={(event) => {
          setFormName(event.target.value);
          setFormDirty(true);
        }}
      />
      <DescriptionTextField
        variant="outlined"
        label="Description Of Work"
        value={formDescription}
        multiline
        minRows={5}
        required
        onChange={(event) => {
          setFormDescription(event.target.value);
          setFormDirty(true);
        }}
      />
      <FormControlLabel
        label={getDeferredLabel()}
        control={
          <Checkbox
            color="primary"
            checked={isDeferred}
            onChange={() => {
              setIsDeferred((prevValue) => !prevValue);
              setFormDirty(true);
            }}
          />
        }
      />
      <BillingCodeField
        label="Billing Code (optional)"
        size="small"
        variant="outlined"
        value={formBillingCode ?? ""}
        onChange={(event) => {
          setFormBillingCode(event.target.value);
          setFormDirty(true);
        }}
      />
      {!isDeferred && (
        <div>
          <FormControlLabel
            label={isFixedFee ? "Fixed fee" : "Fixed Fee?"}
            labelPlacement="end"
            control={
              <Checkbox
                color="primary"
                checked={isFixedFee}
                onChange={() => {
                  setIsFixedFee((prevValue) => !prevValue);
                  setFormDirty(true);
                }}
              />
            }
          />
          <FeeRate container direction="row" alignItems="center">
            <Grid size="grow">
              <CurrencyInput
                value={formFeeRate}
                size="small"
                onChange={(currency: Currency, amount: number) => {
                  setFormFeeRate(new Money(amount, currency ?? "USD"));
                  setFormDirty(true);
                }}
              />
            </Grid>
            {!isFixedFee && (
              <>
                <PerSymbol>/</PerSymbol>
                <Grid size="grow">
                  <TextField
                    label="Unit"
                    size="small"
                    value={formFeeUnit}
                    onChange={(event) => {
                      setFormFeeUnit(event.target.value);
                      setFormDirty(true);
                    }}
                  />
                </Grid>
              </>
            )}
          </FeeRate>
        </div>
      )}
      <ButtonContainer>
        <Button
          color="primary"
          variant="contained"
          disabled={!formDirty || !formValid()}
          startIcon={<SaveIcon />}
          onClick={category ? () => handleSaveCategoryClicked(category.name) : handleAddCategoryClicked}
        >
          {category ? "Save" : "Add"}
        </Button>
      </ButtonContainer>
    </FormContainer>
  );
}
